/**
 * Ad-hoc admin reward — issue a voucher of an admin-chosen amount to an active member, with no
 * pre-configured CTA.
 *
 * The SERVER is the trust boundary. issue_adhoc_reward_tx re-checks the caller's role and is_active
 * itself and is EXECUTE-granted to service_role alone; the client-side gate here and on the route
 * exists so a non-admin never sees a control they'd be refused for, not to authorize anything.
 */
import { supabase } from "./supabase";

/** The RPC accepts 'event' and 'offer' too; this surface offers the two the owner asked for. */
export type AdhocScopeMode = "unscoped" | "retailer";

/** issue_adhoc_reward_tx accepts ONLY these periods (c_allowed_days). Anything else is refused. */
export const ALLOWED_VALIDITY_DAYS = [30, 60, 90, 180, 365] as const;

export type AdhocRewardRequest = {
  contactId: string;
  amount: number;
  validityDays: number;
  scopeMode: AdhocScopeMode;
  /** Required when scopeMode is 'retailer' — a locations.id that HAS a facility_id. */
  scopeRefId?: string | null;
  notes?: string | null;
  idempotencyKey: string;
  overrideDailyLimit?: boolean;
};

export type AdhocRewardResult = {
  voucherCode: string;
  qrImageUrl: string;
  issuanceId: string;
  duplicate: boolean;
  email?: { status?: string; accepted?: boolean; reason?: string } | null;
};

export class AdhocRewardError extends Error {
  readonly status: number;
  /** Machine code the UI keys on — notably daily_limit_reached, which offers an override. */
  readonly code: string | null;
  constructor(message: string, status: number, code: string | null) {
    super(message);
    this.name = "AdhocRewardError";
    this.status = status;
    this.code = code;
  }
}

export async function issueAdhocReward(
  req: AdhocRewardRequest,
): Promise<AdhocRewardResult> {
  const { data: sessionData } = await supabase.auth.getSession();
  const token = sessionData.session?.access_token;
  if (!token) {
    throw new AdhocRewardError("Your session has expired. Sign in again.", 401, null);
  }

  const res = await supabase.functions.invoke("issue-adhoc-reward", {
    body: {
      contactId: req.contactId,
      scopeMode: req.scopeMode,
      scopeRefId: req.scopeMode === "retailer" ? req.scopeRefId : undefined,
      amount: req.amount,
      validityDays: req.validityDays,
      notes: req.notes ?? null,
      idempotencyKey: req.idempotencyKey,
      overrideDailyLimit: req.overrideDailyLimit === true,
    },
  });

  if (res.error) {
    // supabase-js hides the response body on a non-2xx. Read it so the admin sees the server's
    // actual reason ("This member already received an ad-hoc reward today") rather than
    // "Edge Function returned a non-2xx status code", which tells them nothing.
    let message = res.error.message ?? "The reward could not be issued.";
    let status = 500;
    let code: string | null = null;
    const ctx = (res.error as { context?: Response }).context;
    if (ctx && typeof ctx.json === "function") {
      try {
        const body = await ctx.json();
        if (body?.error) message = String(body.error);
        if (body?.code) code = String(body.code);
        status = ctx.status ?? status;
      } catch {
        // keep the generic message
      }
    }
    throw new AdhocRewardError(message, status, code);
  }

  return res.data as AdhocRewardResult;
}

export type AdhocIssuance = {
  id: string;
  issued_at: string;
  actor_user_id: string | null;
  actor_name: string | null;
  contact_id: string;
  contact_name: string | null;
  membership_id: string | null;
  recipient_email: string;
  voucher_id: string;
  voucher_code: string;
  voucher_type: string;
  amount: number;
  admin_notes: string | null;
  scope_mode: string | null;
  scope_label: string | null;
  validity_days: number | null;
  expires_at: string | null;
  daily_limit_overridden: boolean;
  voucher_status: string | null;
  is_redeemed: boolean;
  redeemed_at: string | null;
};

/**
 * Read the issuance ledger. The RPC re-checks the caller is an active admin and raises
 * not_authorized otherwise — `authenticated` holding EXECUTE is not the gate.
 */
export async function getAdhocRewardIssuances(
  limit = 50,
  offset = 0,
  contactId: string | null = null,
): Promise<AdhocIssuance[]> {
  const { data, error } = await supabase.rpc("get_adhoc_reward_issuances", {
    p_limit: limit,
    p_offset: offset,
    p_contact_id: contactId,
  });
  if (error) {
    throw new AdhocRewardError(
      error.message ?? "Could not load the issuance history.",
      403,
      null,
    );
  }
  return (data ?? []) as AdhocIssuance[];
}

export type RetailLocation = {
  id: string;
  name: string;
  city: string | null;
  state: string | null;
};

/**
 * Locations a retail voucher can actually be bound to.
 *
 * facility_id is the ONLY thing redeem_voucher_guarded checks for a retail voucher, so the RPC
 * refuses a location without one (scope_location_not_scopable) — issuing anyway would produce a
 * voucher whose email claims a restriction that does not exist. 7 of 25 live PROD locations have no
 * facility_id, so they are filtered out here rather than offered and then rejected on submit.
 */
export async function getScopableRetailLocations(): Promise<RetailLocation[]> {
  const { data, error } = await supabase
    .from("locations")
    .select("id, name, city, state, facility_id")
    .not("facility_id", "is", null)
    .neq("facility_id", "")
    .or("is_archived.is.null,is_archived.eq.false")
    .order("name");
  if (error) {
    throw new AdhocRewardError(
      error.message ?? "Could not load locations.",
      500,
      null,
    );
  }
  return (data ?? []).map((l) => ({
    id: l.id as string,
    name: l.name as string,
    city: (l.city ?? null) as string | null,
    state: (l.state ?? null) as string | null,
  }));
}

/**
 * The current cap, read from the server. Hardcoding it meant a deploy to change a live risk control;
 * it is now a system_config row. Enforcement is server-side regardless — this is for the UI so an
 * admin is not allowed to type an amount that will be refused after they commit to it.
 */
export async function getAdhocRewardMaxAmount(): Promise<number> {
  const { data, error } = await supabase.rpc("adhoc_reward_max_amount");
  if (error) {
    throw new AdhocRewardError(
      error.message ?? "Could not read the reward cap.",
      503,
      null,
    );
  }
  return Number(data ?? 0);
}
